import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, ChevronUp, ExternalLink, X } from 'lucide-react'
import { getMediaEmbedUrl } from '@/lib/mediaUtils'
import { MediaThumbnail } from '@/components/media/MediaThumbnail'
import { trackMediaEvent } from '@/lib/analytics/mediaEvents'
import type { MediaVideo } from '@/types/media'
import { cn } from '@/lib/utils'

interface MediaReelsViewerProps {
  videos: MediaVideo[]
  startIndex?: number
  onClose: () => void
}

function lockPageScroll() {
  const scrollY = window.scrollY
  const html = document.documentElement
  const body = document.body

  html.style.overflow = 'hidden'
  body.style.overflow = 'hidden'
  body.style.position = 'fixed'
  body.style.top = `-${scrollY}px`
  body.style.left = '0'
  body.style.right = '0'
  body.style.width = '100%'

  return () => {
    html.style.overflow = ''
    body.style.overflow = ''
    body.style.position = ''
    body.style.top = ''
    body.style.left = ''
    body.style.right = ''
    body.style.width = ''
    window.scrollTo(0, scrollY)
  }
}

function triggerIframePlayAttempt(iframe: HTMLIFrameElement | null) {
  if (!iframe) return

  iframe.focus({ preventScroll: true })

  const rect = iframe.getBoundingClientRect()
  const clientX = rect.left + rect.width / 2
  const clientY = rect.top + rect.height * 0.45

  for (const type of ['pointerdown', 'pointerup', 'click'] as const) {
    iframe.dispatchEvent(
      new PointerEvent(type, {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX,
        clientY,
        pointerId: 1,
        pointerType: 'mouse',
        isPrimary: true,
      }),
    )
  }
}

function InstagramEmbed({ video, isActive }: { video: MediaVideo; isActive: boolean }) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const embedUrl = isActive ? getMediaEmbedUrl(video, true) : null

  useLayoutEffect(() => {
    if (!isActive || !embedUrl) return
    triggerIframePlayAttempt(iframeRef.current)
  }, [embedUrl, isActive])

  if (!isActive || !embedUrl) {
    return (
      <div className="relative h-full w-full bg-black">
        <MediaThumbnail
          src={video.thumbnail}
          alt={video.title}
          platform={video.platform}
          socialUrl={video.socialUrl}
          videoId={video.id}
          className="h-full w-full object-cover"
        />
        {!isActive ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <span className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="relative h-full w-full overflow-hidden bg-black">
      <iframe
        key={embedUrl}
        ref={iframeRef}
        title={video.title}
        src={embedUrl}
        className="pointer-events-auto absolute left-1/2 top-1/2 h-[calc(100%+96px)] w-full max-w-none -translate-x-1/2 -translate-y-[calc(50%+8px)] border-0"
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        scrolling="no"
        tabIndex={0}
        onLoad={(event) => {
          triggerIframePlayAttempt(event.currentTarget)
          trackMediaEvent('media_video_play', {
            video_id: video.id,
            video_slug: video.slug,
            platform: video.platform,
            mode: 'reels_viewer',
          })
        }}
      />
    </div>
  )
}

function ReelSlide({
  video,
  isActive,
}: {
  video: MediaVideo
  isActive: boolean
}) {
  return (
    <div className="relative flex h-full w-full items-center justify-center bg-black">
      <div className="relative h-full w-full max-w-[min(100vw,420px)]">
        <InstagramEmbed video={video} isActive={isActive} />

        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-linear-to-t from-black/90 via-black/50 to-transparent p-5 pt-16">
          <p className="text-sm font-semibold text-white line-clamp-2">{video.title}</p>
          {video.category ? (
            <p className="mt-1 text-xs uppercase tracking-wide text-white/70">{video.category.name}</p>
          ) : null}
        </div>

        <a
          href={video.socialUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-5 right-5 z-10 inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-md transition hover:bg-white/25"
          onClick={() =>
            trackMediaEvent('media_video_click', {
              video_id: video.id,
              source: 'reels_open_instagram',
            })
          }
        >
          Open <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  )
}

export function MediaReelsViewer({ videos, startIndex = 0, onClose }: MediaReelsViewerProps) {
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(0, Math.min(startIndex, videos.length - 1)),
  )
  const touchStartY = useRef<number | null>(null)
  const wheelLock = useRef(false)

  const goToIndex = useCallback(
    (index: number) => {
      setActiveIndex(Math.max(0, Math.min(index, videos.length - 1)))
    },
    [videos.length],
  )

  useEffect(() => lockPageScroll(), [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowDown') goToIndex(activeIndex + 1)
      if (e.key === 'ArrowUp') goToIndex(activeIndex - 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeIndex, goToIndex, onClose])

  const onWheel = useCallback(
    (event: React.WheelEvent) => {
      if (wheelLock.current) return
      if (Math.abs(event.deltaY) < 24) return

      wheelLock.current = true
      goToIndex(activeIndex + (event.deltaY > 0 ? 1 : -1))
      window.setTimeout(() => {
        wheelLock.current = false
      }, 450)
    },
    [activeIndex, goToIndex],
  )

  const onTouchStart = (event: React.TouchEvent) => {
    touchStartY.current = event.touches[0]?.clientY ?? null
  }

  const onTouchEnd = (event: React.TouchEvent) => {
    const startY = touchStartY.current
    const endY = event.changedTouches[0]?.clientY
    touchStartY.current = null
    if (startY == null || endY == null) return

    const delta = startY - endY
    if (Math.abs(delta) < 48) return
    goToIndex(activeIndex + (delta > 0 ? 1 : -1))
  }

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] overflow-hidden bg-black touch-none"
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-md transition hover:bg-black/70"
          aria-label="Close reels viewer"
        >
          <X className="h-5 w-5" />
        </button>

        {activeIndex > 0 ? (
          <button
            type="button"
            onClick={() => goToIndex(activeIndex - 1)}
            className="absolute left-1/2 top-3 z-30 -translate-x-1/2 rounded-full bg-white/10 p-2 text-white backdrop-blur-md md:hidden"
            aria-label="Previous reel"
          >
            <ChevronUp className="h-5 w-5" />
          </button>
        ) : null}

        {activeIndex < videos.length - 1 ? (
          <button
            type="button"
            onClick={() => goToIndex(activeIndex + 1)}
            className="absolute bottom-6 left-1/2 z-30 -translate-x-1/2 rounded-full bg-white/10 p-2 text-white backdrop-blur-md md:hidden"
            aria-label="Next reel"
          >
            <ChevronDown className="h-5 w-5" />
          </button>
        ) : null}

        <div className="relative h-[100dvh] w-full overflow-hidden">
          {videos.map((video, index) => (
            <motion.div
              key={video.id}
              className="absolute inset-0 h-[100dvh] w-full"
              initial={false}
              animate={{
                y: `${(index - activeIndex) * 100}%`,
                opacity: Math.abs(index - activeIndex) <= 1 ? 1 : 0,
              }}
              transition={{ type: 'spring', stiffness: 380, damping: 36 }}
            >
              <ReelSlide video={video} isActive={index === activeIndex} />
            </motion.div>
          ))}
        </div>

        {videos.length > 1 ? (
          <div className="pointer-events-none absolute right-3 top-1/2 z-30 hidden -translate-y-1/2 flex-col gap-2 md:flex">
            {videos.map((_, index) => (
              <span
                key={index}
                className={cn(
                  'h-2 w-2 rounded-full transition',
                  index === activeIndex ? 'scale-125 bg-white' : 'bg-white/35',
                )}
              />
            ))}
          </div>
        ) : null}
      </motion.div>
    </AnimatePresence>,
    document.body,
  )
}
